import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './styles.module.scss';
import Button from '../Button/index.ts';
import Icon from '../Icon/index.ts';
import addVariants from '../utils/addVariants/index.ts';
import useBoolean from '../utils/useBoolean/index.ts';
import useSpacing from '../utils/useSpacing/index.ts';

const ICONS = {
  CLOSE: faTimes,
  INFO: faExclamationTriangle,
  WARNING: faExclamationTriangle,
  CRITICAL: faExclamationTriangle
};

export const TYPES = ['info', 'warning', 'critical'];

export const VARIANTS = ['default', 'comfortable', 'dense'];

function Banner({
  children,
  className,
  isDismissable,
  icon: iconFromProps,
  hasIcon,
  onChange,
  onOpen,
  onClose,
  isOpen: isOpenFromProps,
  isOpenByDefault,
  spacing,
  style,
  type,
  variant
}) {
  const { value: isOpen, setFalse: close } = useBoolean(isOpenByDefault, {
    onChange: isOpenFromState => {
      if (isOpenFromState) {
        onOpen();
      } else {
        onClose();
      }

      onChange(isOpenFromState);
    },
    overrideWith: isOpenFromProps
  });
  const spacingStyle = useSpacing(spacing, { style });
  let iconEl;

  if (hasIcon !== false) {
    const icon = iconFromProps || ICONS[type.toUpperCase()];

    iconEl = <Icon className={styles.banner__icon} icon={icon} />;
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={classnames(
        addVariants(
          'banner',
          styles,
          type,
          variant,
          iconEl ? 'has-icon' : 'no-icon',
          isDismissable && 'dismissable'
        ),
        className
      )}
      style={spacingStyle}
    >
      {iconEl}
      <div className={addVariants('banner__content', styles, iconEl ? 'has-icon' : 'no-icon')}>
        {children}
      </div>
      {isDismissable && (
        <Button
          className={styles.banner__cancel}
          variant={['round', 'no-border']}
          color="hover-darken"
          icon={ICONS.CLOSE}
          onClick={close}
        />
      )}
    </div>
  );
}

Banner.propTypes = {
  /**
   * The type of Banner to display
   */
  type: PropTypes.oneOf(TYPES).isRequired,
  /**
   * Anything to render within a Banner
   */
  children: PropTypes.node.isRequired,
  /**
   * Fires on close
   * @deprecated Use `onChange` instead
   */
  onClose: PropTypes.func,
  /**
   * Fires on open
   * @deprecated Use `onChange` instead
   */
  onOpen: PropTypes.func,
  /**
   * Fires on change of open state
   */
  onChange: PropTypes.func,
  /**
   * Controls open state (state must be handled in caller)
   */
  isOpen: PropTypes.bool,
  /**
   * Whether the Banner is dismissable
   */
  isDismissable: PropTypes.bool,
  /**
   * Whether the Banner is open by default
   */
  isOpenByDefault: PropTypes.bool,
  /**
   * Specify custom icon, false to disable icon
   */
  icon: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string.isRequired),
    PropTypes.shape({
      iconName: PropTypes.string.isRequired,
      prefix: PropTypes.string.isRequired
    })
  ]),
  /**
   * Whether the Banner has an icon
   */
  hasIcon: PropTypes.bool,
  /**
   * Decides the variant of the button
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Allow customization of the margin around the `Card`. See `Spacer` Component
   */
  spacing: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    right: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
  }),
  /**
   * Custom styles to apply, use sparingly
   */
  style: PropTypes.shape({}),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string
};

Banner.defaultProps = {
  isDismissable: false,
  variant: 'default',
  className: '',
  hasIcon: true,
  isOpenByDefault: true,
  isOpen: null,
  onChange: () => 0,
  onOpen: () => 0,
  onClose: () => 0,
  spacing: {},
  style: {},
  icon: null
};

export default Banner;
